import React, { useState, useEffect, useRef } from "react";
import "./ChatQuickActions.scss";
import { BvIcon } from "bevi-icon";
import { useChatsContext } from "../../../contexts/ChatsContext";
import { useChats } from "../../../providers/Chats/ChatsProvider";
import { ChatsService } from "../../../services/Chats/ChatsService";
import { ReactComponent as PostAdd } from "../../../assets/img/post-add.svg"
import { useAuth } from "../../../providers/AuthProvider";
import { UserRoleEnum } from "../../../enums/User/UserRoleEnum";
import { useUsers } from "../../../providers/Access/Users/UsersProvider";
import { useChatsTutorial } from "../../../providers/Chats/ChatsTutorialProvider";
import { useNavigate } from "react-router-dom";
import { useChatHighlights } from "../../../providers/ChatHighlights/ChatHighlightsProvider";


export type ChildrenType = {
  id: string;
  label: string;
  payload: string;
};

export type MenuContentType = {
  id: string;
  label: string;
  icon: string;
  children?: ChildrenType[];
};

const menuContent: MenuContentType[] = [
  {
    id: "concluir-chat",
    label: "Nova Conversa",
    icon: "postAdd",
    children: undefined,
  },
  {
    id: "anexar",
    label: "Anexar",
    icon: "attachFile",
    children: undefined,
  },
  {
    id: "reiniciar-tutorial",
    label: "Ver Tutorial Novamente",
    icon: "autorenew",
    children: undefined,
  },
  // {
  //   id: "inss",
  //   label: "INSS",
  //   icon: "bank",
  //   children: [
  //     {
  //       id: "bancos-produtos-ativos",
  //       label: "Bancos e Produtos Ativos",
  //       payload: "Quais bancos/produtos estão ativos no INSS?",
  //     },
  //   ],
  // },
];





type ChatQuickActionsProps = {
  onSelect?: (payload: string) => void;
  isOpen: boolean;
  onClose: () => void;
  buttonRef: React.RefObject<HTMLButtonElement | null>;
  onElementSelected?: (child: ChildrenType, parent: MenuContentType) => void;
  onAttachClick?: () => void;
};

const ChatQuickActions = ({
  isOpen,
  onClose,
  buttonRef,
  onElementSelected,
  onSelect,
  onAttachClick,
}: ChatQuickActionsProps) => {
  const menuRef = useRef<HTMLDivElement>(null);
  const [activeItem, setActiveItem] = useState<string | null>(null);
  const { state, chat, setState, setChat } = useChats();
  const chatsService = new ChatsService();
  const { handleResetTutorial } = useUsers();
  const { setIsStreaming, resetTutorial } = useChatsTutorial();
  const navigate = useNavigate();

  const { userLogged } = useAuth();
  const { activeHighlight, handleOpenModal } = useChatHighlights();

  const isAdmin =
    userLogged &&
    userLogged?.role == UserRoleEnum.ADMIN;

  const USER_VISIBLE_IDS = ["concluir-chat", "reiniciar-tutorial"];

  const filteredMenu = menuContent.filter(
    (item) => isAdmin || USER_VISIBLE_IDS.includes(item.id)
  );

  // UseEffect para verificar se deve abrir o dropdown para cima ou para baixo 
  // com a variável "openAbove"
  //
  // Atualmente está fazendo essa ação com o useChatsContext(), caso não use
  // o useChatsContext(), pode utilizar esse useEffect para ter o mesmo resultado 
  // 
  // const [openAbove, setOpenAbove] = useState(false);
  // useEffect(() => {
  //   if (isOpen && buttonRef.current && menuRef.current) {
  //     const buttonRect = buttonRef.current.getBoundingClientRect();
  //     const menuHeight = 300; // Altura estimada do menu
  //     const windowHeight = window.innerHeight;

  //     const spaceBelow = windowHeight - buttonRect.bottom;
  //     const shouldOpenAbove =
  //       spaceBelow < menuHeight && buttonRect.top > menuHeight;

  //     setOpenAbove(shouldOpenAbove);
  //   }
  // }, [isOpen, buttonRef]);

  // Fecha o menu ao clicar fora
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        onClose();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen, onClose, buttonRef]);

  // Fecha o menu ao pressionar ESC
  useEffect(() => {
    if (!isOpen) return;

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
        setActiveItem(null);
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [isOpen, onClose]);

  const handleItemHover = (itemId: string) => {
    setActiveItem(itemId);
  };

  const handleItemLeave = () => {
    setActiveItem(null);
  };

  const handleChildClick = (child: ChildrenType, parent: MenuContentType) => {
    if (onElementSelected) {
      onElementSelected(child, parent);
    }
    onClose();
    setActiveItem(null);
  };

  const handleFinishChat = async () => {
    if (!chat?.chat_id) return;

    try {
      await chatsService.finish(chat.chat_id);

      setChat(null);
      setState("default");
      onClose();
    } catch (error) {
      console.error("Erro ao concluir chat", error);
    }
  }

  const handleRestartTutorial = async () => {
    try {
      await handleResetTutorial();

      resetTutorial();
      setIsStreaming(false);

      if (chat?.chat_id) {
        await chatsService.finish(chat.chat_id);
      }

      setChat(null);
      setState("default");
      onClose();

      navigate("/chats/tutorial");
    } catch (error) {
      console.error("Erro ao resetar o tutorial", error);
    }
  }

  if (!isOpen) return null;

  return (
    <div
      ref={menuRef}
      className={`chat-quick-actions ${state === 'started' ? "chat-quick-actions--above" : ""}`}
    >
      <div className="chat-quick-actions__main-content">
        <ul className="chat-quick-actions__main-list">
          {filteredMenu.map((item: MenuContentType) => (
            <li key={item.id} className="position-relative w-100">
              <button
                className="chat-quick-actions__item-button"
                onMouseEnter={() => item.children && handleItemHover(item.id)}
                onMouseLeave={handleItemLeave}
                onClick={() => {
                  if (item.id === "anexar") {
                    onAttachClick?.();
                    onClose();
                  }
                  else if (item.id === "concluir-chat") {
                    handleFinishChat();
                  } else if (item.id === "reiniciar-tutorial") {
                    handleRestartTutorial();
                  }
                }}
                data-active={activeItem === item.id ? true : false}
              >
                <div className="group d-flex align-items-center">
                  {item?.icon === "postAdd" ? (<PostAdd className="align-items-center" style={{ fill: "#222343", width: "16px", height: "16px", marginRight: "2px" }} />) : (<BvIcon name={item.icon} size={1} style={{ marginRight: "2px" }} />)}

                  {item.label}
                </div>
                {item.children && (
                  <BvIcon name="chevronRight" weight={600} size={1} />
                )}
              </button>

              {activeItem === item.id && (
                <div
                  className={`chat-quick-actions__child-content ${state === 'started' ? "chat-quick-actions__child-content--above" : ""}`}
                  onMouseEnter={() => setActiveItem(item.id)}
                  onMouseLeave={handleItemLeave}
                >
                  <ul>
                    {item.children?.map((child) => (
                      <li key={child.id}>
                        <button
                          className="chat-quick-actions__item-button"
                          onClick={() => handleChildClick(child, item)}

                        >
                          {child.label}
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </li>
          ))}

          {activeHighlight && (
            <li className="position-relative w-100">
              <button
                className="chat-quick-actions__item-button"
                onClick={() => {
                  handleOpenModal();
                  onClose();
                }}
              >
                <div className="group d-flex align-items-center">
                  <BvIcon name="notifications" size={1} style={{ marginRight: "2px" }} />
                  Ver destaque
                </div>
              </button>
            </li>
          )}
        </ul>
      </div>
    </div>
  );
};

export default ChatQuickActions;
